import React, { useMemo, useState } from "react";
import {
  BadgeCheck, Zap, ArrowUpRight,
  Send, Repeat2, MessageSquare, Inbox, BriefcaseBusiness, Telescope,
  Clock, ListTodo, Library, Cpu, ScrollText, Check, Pencil, Loader2, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { Breadcrumb } from "@/shared/components/Breadcrumb";
import { SearchInput, PageHeader } from "@/shared/components/ui-kit";
import PixelAvatar from "@/shared/components/PixelAvatar";
import { APP_ICONS, APP_LABEL } from "@/shared/lib/app-icons";
import { ViaIcon } from "@/shared/components/ViaIcon";
import { catalogFor } from "@/shared/lib/interface-catalog";
import { TEMPLATES_CATALOG, TEMPLATE_CATEGORIES } from "@/shared/lib/template-catalog";
import type { LucideIcon } from "lucide-react";
import type {
  Template, TemplateApp, TemplateCapability, TemplateCapabilityIcon,
} from "@/shared/types";

/** How far along the "Use template" click is, per template. */
type UseStatus = "idle" | "busy" | "done";

const CAP_ICONS: Record<TemplateCapabilityIcon, LucideIcon> = {
  send: Send,
  repeat: Repeat2,
  message: MessageSquare,
  inbox: Inbox,
  briefcase: BriefcaseBusiness,
  telescope: Telescope,
};

/* ------------------------------- atoms ---------------------------------- */

function OfficialBadge({ size = 13 }: { size?: number }) {
  return (
    <span title="Officially maintained by Definable" className="shrink-0 flex items-center">
      {/* fill as an attribute, not inline CSS — see the note in LeftSidebar. */}
      <BadgeCheck
        style={{ width: size, height: size }}
        fill="var(--text-primary)"
        className="text-[var(--surface-sunk)]"
        strokeWidth={1.75}
      />
    </span>
  );
}

function AppStack({ apps, max = 4 }: { apps: TemplateApp[]; max?: number }) {
  const ids = apps.map((a) => a.id).slice(0, max);
  return (
    <div className="flex -space-x-1.5">
      {ids.map((id) => (
        <div
          key={id}
          title={APP_LABEL[id]}
          className="w-[18px] h-[18px] rounded-full bg-[var(--surface)] ring-[1.5px] ring-[var(--canvas)] flex items-center justify-center overflow-hidden shadow-[0_0_0_0.5px_rgba(0,0,0,0.08)]"
        >
          <img src={APP_ICONS[id]} alt={APP_LABEL[id]} className="w-[11px] h-[11px]" />
        </div>
      ))}
    </div>
  );
}

function ScheduleChip({ children }: { children?: React.ReactNode }) {
  return (
    <span className="shrink-0 flex items-center gap-[3px] font-mono text-[9.5px] font-medium tracking-[0.04em] px-[6px] py-[2px] rounded-full text-[var(--text-tertiary)] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline-strong)]">
      <Clock className="w-[8px] h-[8px]" strokeWidth={2} />
      {children}
    </span>
  );
}

function UseTemplateButton({ template, small, status = "idle", onUse }: {
  template: Template;
  small?: boolean;
  status?: UseStatus;
  onUse: (template: Template) => void;
}) {
  const base = `press shrink-0 rounded-[8px] font-medium ${
    small ? "px-3 py-[5px] text-[12.5px]" : "px-3.5 py-[7px] text-[13px]"
  }`;
  const iconCls = small ? "w-3 h-3" : "w-3.5 h-3.5";

  if (status === "busy") {
    return (
      <button
        disabled
        onClick={(e) => e.stopPropagation()}
        className={`${base} flex items-center gap-1.5 bg-[var(--surface-sunk)] text-[var(--text-tertiary)] ring-1 ring-[var(--hairline)] cursor-default`}
      >
        <Loader2 className={`${iconCls} animate-spin`} strokeWidth={2} />
        Setting up…
      </button>
    );
  }

  if (status === "done") {
    return (
      <button
        disabled
        onClick={(e) => e.stopPropagation()}
        className={`${base} flex items-center gap-1 bg-[var(--success-bg)] text-[var(--success)] ring-1 ring-[var(--success-ring)] cursor-default`}
      >
        <Check className={iconCls} strokeWidth={2} />
        Added
      </button>
    );
  }

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onUse(template);
      }}
      className={`${base} bg-[var(--brand)] text-[var(--brand-foreground)] hover:bg-[var(--brand-hover)]`}
    >
      Use template
    </button>
  );
}

/* ----------------------------- grid view -------------------------------- */

function FeaturedCard({ template, onOpen, status, onUse }: {
  template: Template;
  onOpen: (id: string) => void;
  status: UseStatus;
  onUse: (template: Template) => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(template.id)}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(template.id); }}
      className="card-hover press w-full cursor-pointer text-left rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] p-5 flex flex-col gap-4"
    >
      <div className="flex items-center gap-1.5 section-label">
        Featured template
      </div>
      <div className="flex flex-wrap items-start gap-4">
        <PixelAvatar seed={template.seed} hue={template.hue} size={44} ring rounded={11} />
        <div className="min-w-0 flex-1 basis-[200px]">
          <div className="flex items-center gap-1.5">
            <span className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
              {template.name}
            </span>
            {template.official && <OfficialBadge />}
          </div>
          <p className="text-[13px] leading-[1.6] text-[var(--text-secondary)] mt-1 mb-0 max-w-[560px]">
            {template.tagline}
          </p>
        </div>
        <UseTemplateButton template={template} status={status} onUse={onUse} />
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {template.capabilities.map((c) => {
          const Icon = CAP_ICONS[c.icon];
          return (
            <span
              key={c.id}
              className="flex items-center gap-1.5 px-2.5 py-[4px] rounded-full bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] text-[12px] text-[var(--text-secondary)]"
            >
              <Icon className="w-3 h-3 text-[var(--text-tertiary)]" strokeWidth={1.75} />
              {c.name}
            </span>
          );
        })}
        <span className="ml-auto flex items-center gap-2 text-[11.5px] text-[var(--text-tertiary)]">
          <AppStack apps={template.apps} />
        </span>
      </div>
    </div>
  );
}

function TemplateCard({ template, onOpen, status, onUse }: {
  template: Template;
  onOpen: (id: string) => void;
  status: UseStatus;
  onUse: (template: Template) => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(template.id)}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen(template.id); }}
      className="card-hover press cursor-pointer text-left rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] p-4 flex flex-col gap-3 min-w-0"
    >
      <div className="flex items-start gap-3">
        <PixelAvatar seed={template.seed} hue={template.hue} size={36} ring rounded={9} />
        <div className="min-w-0 flex-1">
          {/* The name wraps rather than truncates: beside "Use template" a phone leaves it ~100px */}
          <div className="flex items-start gap-1.5 min-w-0">
            <span className="text-[14px] font-medium text-[var(--text-primary)] tracking-[-0.005em] break-words min-w-0">
              {template.name}
            </span>
            {template.official && <span className="shrink-0 mt-[3px]"><OfficialBadge size={12} /></span>}
          </div>
          <div className="text-[11.5px] text-[var(--text-tertiary)] mt-[1px]">{template.category}</div>
        </div>
        <UseTemplateButton template={template} small status={status} onUse={onUse} />
      </div>
      <p className="text-[12.5px] leading-[1.55] text-[var(--text-secondary)] line-clamp-2 m-0">
        {template.tagline}
      </p>
      <div className="flex items-center gap-3 text-[11.5px] text-[var(--text-tertiary)] mt-auto">
        <span className="inline-flex items-center gap-1 whitespace-nowrap">
          <Zap className="w-3 h-3" strokeWidth={1.75} />
          {template.capabilities.length} capabilities
        </span>
        {(template.skills || []).length > 0 && (
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <Sparkles className="w-3 h-3" strokeWidth={1.75} />
            {template.skills!.length} skills
          </span>
        )}
        <span className="ml-auto"><AppStack apps={template.apps} max={3} /></span>
      </div>
    </div>
  );
}

/* `template.skills` / `template.interfaces` are optional on the type, and each
   read below sits behind a `(x || []).length > 0` guard — the `!`s say what the
   guard already proved rather than adding a new one. */

function TemplatesGrid({ onOpen, statusFor, onUse }: {
  onOpen: (id: string) => void;
  statusFor: (id: string) => UseStatus;
  onUse: (template: Template) => void;
}) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TEMPLATES_CATALOG.filter(
      (t) =>
        (category === "All" || t.category === category) &&
        (!q ||
          t.name.toLowerCase().includes(q) ||
          t.tagline.toLowerCase().includes(q) ||
          t.capabilities.some((c) => c.name.toLowerCase().includes(q)))
    );
  }, [query, category]);

  const featured = TEMPLATES_CATALOG[0];
  const showFeatured =
    !query.trim() && (category === "All" || featured.category === category);
  const rest = filtered.filter((t) => !showFeatured || t.id !== featured.id);
  const countFor = (c: string) =>
    c === "All"
      ? TEMPLATES_CATALOG.length
      : TEMPLATES_CATALOG.filter((t) => t.category === c).length;

  return (
    <>
      <PageHeader
        title={<Breadcrumb>Templates</Breadcrumb>}
      >
        <SearchInput
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search templates"
          className="w-full min-w-[120px] max-w-[240px]"
        />
      </PageHeader>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[1160px] mx-auto px-4 md:px-6 py-6 flex items-start gap-7 fade-in-up">
          {/* Category rail */}
          <nav className="w-[172px] shrink-0 sticky top-0 hidden md:block">
            <div className="section-label pb-2 px-2">Categories</div>
            <div className="space-y-[2px]">
              {TEMPLATE_CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`press w-full flex items-center gap-2 px-2 py-[6px] rounded-[8px] text-left text-[13px] tracking-[-0.005em] ${
                    c === category
                      ? "bg-[var(--accent-active)] text-[var(--text-primary)] font-medium"
                      : "text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
                  }`}
                >
                  <span className="flex-1 truncate">{c}</span>
                  <span className="text-[11px] tabular-nums text-[var(--text-quaternary)]">
                    {countFor(c)}
                  </span>
                </button>
              ))}
            </div>
            <p className="px-2 pt-4 text-[11.5px] leading-[1.6] text-[var(--text-quaternary)]">
              Templates are pre-built agents. Add one, then rename it, rewrite its
              instructions, or swap apps — nothing is locked.
            </p>
          </nav>

          {/* Cards */}
          <div className="flex-1 min-w-0 space-y-4">
            {showFeatured && (
              <FeaturedCard template={featured} onOpen={onOpen} status={statusFor(featured.id)} onUse={onUse} />
            )}
            {/* Beside the sidebar and the categories rail, two columns only fit from xl */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
              {rest.map((t) => (
                <TemplateCard key={t.id} template={t} onOpen={onOpen} status={statusFor(t.id)} onUse={onUse} />
              ))}
            </div>
            {filtered.length === 0 && (
              <div className="py-16 text-center text-[13px] text-[var(--text-tertiary)]">
                No templates match “{query}”.
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/* ---------------------------- detail view ------------------------------- */

function CapabilityCard({ capability }: { capability: TemplateCapability }) {
  const Icon = CAP_ICONS[capability.icon];
  const copyPrompt = () => {
    navigator.clipboard?.writeText(capability.prompt);
    toast("Prompt copied", { description: "Paste it into any chat with this agent." });
  };
  return (
    <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] p-4 flex flex-col gap-3 min-w-0">
      <div className="flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-[8px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] flex items-center justify-center shrink-0">
          <Icon className="w-3.5 h-3.5 text-[var(--text-secondary)]" strokeWidth={1.75} />
        </div>
        <div className="min-w-0">
          <div className="text-[14px] font-medium text-[var(--text-primary)] tracking-[-0.005em]">
            {capability.name}
          </div>
          <div className="text-[12px] text-[var(--text-tertiary)] mt-[1px] truncate">
            {capability.description}
          </div>
        </div>
      </div>
      {/* ChatGPT-style starter prompt: click to copy */}
      <button
        onClick={copyPrompt}
        title="Copy prompt"
        className="press group text-left flex items-start gap-2 rounded-[10px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] hover:ring-[var(--hairline-strong)] px-3 py-2.5"
      >
        <span className="flex-1 text-[12.5px] leading-[1.55] text-[var(--text-secondary)]">
          “{capability.prompt}”
        </span>
        <ArrowUpRight
          className="w-3.5 h-3.5 shrink-0 mt-[1px] text-[var(--text-quaternary)] group-hover:text-[var(--text-primary)] transition-colors"
          strokeWidth={1.75}
        />
      </button>
    </div>
  );
}

function SectionHeading({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex items-baseline gap-2 pb-2.5">
      <h2 className="text-[14px] font-semibold tracking-[-0.01em] m-0">{title}</h2>
      {hint && <span className="text-[11.5px] text-[var(--text-tertiary)]">{hint}</span>}
    </div>
  );
}

function InsideRow({ icon: Icon, label, detail }: {
  icon: LucideIcon;
  label: string;
  detail: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-[9px] border-t hairline first:border-t-0">
      <Icon className="w-3.5 h-3.5 shrink-0 text-[var(--text-tertiary)]" strokeWidth={1.75} />
      <span className="flex-1 text-[13px] text-[var(--text-secondary)]">{label}</span>
      <span className="text-[11.5px] text-[var(--text-tertiary)] tabular-nums text-right">{detail}</span>
    </div>
  );
}

function TemplateDetail({ template, onBack, status, onUse }: {
  template: Template;
  onBack: () => void;
  status: UseStatus;
  onUse: (template: Template) => void;
}) {
  const [showFullInstructions, setShowFullInstructions] = useState(false);

  return (
    <>
      <PageHeader
        title={
          <Breadcrumb parent={{ label: "Templates", onClick: onBack }}>
            <PixelAvatar seed={template.seed} hue={template.hue} size={20} ring rounded={5} />
            <span className="truncate">{template.name}</span>
            {template.official && <OfficialBadge size={12} />}
          </Breadcrumb>
        }
      >
        <UseTemplateButton template={template} status={status} onUse={onUse} />
      </PageHeader>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[1080px] mx-auto px-4 md:px-6 py-6 flex items-start gap-7 fade-in-up">
          {/* Main column */}
          <div className="flex-1 min-w-0 space-y-7">
            {/* Hero */}
            <div className="flex items-start gap-4">
              <PixelAvatar seed={template.seed} hue={template.hue} size={52} ring rounded={13} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <h1 className="text-[17px] font-semibold tracking-[-0.01em] m-0">{template.name}</h1>
                  {template.official && <OfficialBadge />}
                </div>
                <p className="text-[13px] leading-[1.6] text-[var(--text-secondary)] mt-1 mb-0">
                  {template.tagline}
                </p>
                <div className="flex items-center gap-3 mt-2 text-[11.5px] text-[var(--text-tertiary)]">
                  <span>{template.category}</span>
                  <span className="w-[3px] h-[3px] rounded-full bg-[var(--text-quaternary)]" />
                  <span className="font-mono text-[10.5px]">{template.model}</span>
                </div>
              </div>
            </div>

            {/* Capabilities */}
            <section>
              <SectionHeading
                title="What it can do"
                hint="Click a prompt to copy it — every one is a real task this agent handles"
              />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {template.capabilities.map((c) => (
                  <CapabilityCard key={c.id} capability={c} />
                ))}
              </div>
            </section>

            {/* Instructions */}
            <section>
              <SectionHeading title="Instructions" hint="The agent's system prompt — yours to rewrite" />
              <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] p-4">
                <p
                  className={`text-[13px] leading-[1.7] text-[var(--text-secondary)] m-0 whitespace-pre-line ${
                    showFullInstructions ? "" : "line-clamp-4"
                  }`}
                >
                  {template.instructions}
                </p>
                <div className="flex items-center justify-between mt-3">
                  <button
                    onClick={() => setShowFullInstructions((v) => !v)}
                    className="press text-[12.5px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                  >
                    {showFullInstructions ? "Show less" : "Show more"}
                  </button>
                  <span className="flex items-center gap-1.5 text-[11.5px] text-[var(--text-quaternary)]">
                    <Pencil className="w-3 h-3" strokeWidth={1.75} />
                    Editable after you add it
                  </span>
                </div>
              </div>
            </section>

            {/* Automations */}
            {template.tasks.length > 0 && (
              <section>
                <SectionHeading title="Runs on a schedule" hint="Included tasks — pause or edit any of them" />
                <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] overflow-hidden">
                  {template.tasks.map((t) => (
                    <div key={t.id} className="flex items-center gap-3 px-4 py-3 border-t hairline first:border-t-0">
                      <ListTodo className="w-3.5 h-3.5 shrink-0 text-[var(--text-tertiary)]" strokeWidth={1.75} />
                      <div className="min-w-0 flex-1">
                        <div className="text-[14px] font-medium text-[var(--text-primary)] tracking-[-0.005em]">
                          {t.name}
                        </div>
                        <div className="text-[12.5px] text-[var(--text-secondary)] mt-[1px] truncate">
                          {t.detail}
                        </div>
                      </div>
                      <ScheduleChip>{t.schedule}</ScheduleChip>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Right rail — the pack manifest */}
          <aside className="w-[280px] shrink-0 sticky top-0 hidden lg:flex flex-col gap-4">
            <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] p-4 space-y-3">
              <UseTemplateButton template={template} status={status} onUse={onUse} />
              <p className="text-[12px] leading-[1.6] text-[var(--text-tertiary)] m-0">
                Adds a ready-to-run agent to <span className="text-[var(--text-secondary)] font-medium">Your agents</span>.
                Everything below comes with it — and everything stays editable.
              </p>
            </div>

            <div>
              <div className="section-label pb-2 px-1">What's inside</div>
              <div className="rounded-[12px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] overflow-hidden">
                <InsideRow icon={Zap} label="Capabilities" detail={template.capabilities.length} />
                {(template.skills || []).length > 0 && (
                  <InsideRow icon={Sparkles} label="Skills" detail={template.skills!.length} />
                )}
                <InsideRow icon={ScrollText} label="Instructions" detail={<Check className="w-3 h-3" strokeWidth={2} />} />
                {template.tasks.length > 0 && (
                  <InsideRow icon={ListTodo} label="Scheduled tasks" detail={template.tasks.length} />
                )}
                {template.knowledge.length > 0 && (
                  <InsideRow icon={Library} label="Knowledge slots" detail={template.knowledge.length} />
                )}
                <InsideRow icon={Cpu} label="Default model" detail={template.model} />
              </div>
            </div>

            <div>
              <div className="section-label pb-2 px-1">Connect apps</div>
              <div className="rounded-[12px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] overflow-hidden">
                {template.apps.map((a) => (
                  <div key={a.id} className="flex items-center gap-2.5 px-3 py-[9px] border-t hairline first:border-t-0">
                    <div className="w-[22px] h-[22px] rounded-[6px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] flex items-center justify-center shrink-0">
                      <img src={APP_ICONS[a.id]} alt={APP_LABEL[a.id]} className="w-[12px] h-[12px]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] text-[var(--text-primary)] tracking-[-0.005em]">
                        {APP_LABEL[a.id]}
                      </div>
                      <div className="text-[11px] text-[var(--text-tertiary)] truncate">{a.requirement}</div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="px-1 pt-2 text-[11px] leading-[1.6] text-[var(--text-quaternary)] m-0">
                Connect them from Apps — the agent asks the first time it needs one.
              </p>
            </div>

            {(template.interfaces || []).length > 0 && (
              <div>
                <div className="section-label pb-2 px-1">Ships with interfaces</div>
                <div className="rounded-[12px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] overflow-hidden">
                  {template.interfaces!.map((x) => {
                    const meta = catalogFor(x.type);
                    if (!meta) return null;
                    return (
                      <div key={x.type} className="flex items-center gap-2.5 px-3 py-[9px] border-t hairline first:border-t-0">
                        <div className="w-[22px] h-[22px] rounded-[6px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] flex items-center justify-center shrink-0">
                          <ViaIcon type={x.type} size={12} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] text-[var(--text-primary)] tracking-[-0.005em]">{meta.name}</div>
                          <div className="text-[11px] text-[var(--text-tertiary)] truncate">Live the moment the agent is created</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {template.knowledge.length > 0 && (
              <div>
                <div className="section-label pb-2 px-1">Bring your own context</div>
                <div className="rounded-[12px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] overflow-hidden">
                  {template.knowledge.map((k) => (
                    <div key={k.id} className="px-3 py-[9px] border-t hairline first:border-t-0">
                      <div className="text-[13px] text-[var(--text-primary)] tracking-[-0.005em]">{k.name}</div>
                      <div className="text-[11px] text-[var(--text-tertiary)]">{k.hint}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="px-1 text-[11px] text-[var(--text-quaternary)] flex items-center gap-1.5">
              <span>{template.version}</span>
              <span className="w-[3px] h-[3px] rounded-full bg-[var(--text-quaternary)]" />
              <span>by Definable</span>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}

/* -------------------------------- page ---------------------------------- */

export default function TemplatesPage({ onUseTemplate }: {
  /** Creates the agent; resolves false when it could not (the failure is already reported). */
  onUseTemplate: (template: Template) => Promise<boolean>;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [usedIds, setUsedIds] = useState(() => new Set<string>()); // session-scoped "Added"
  const [busyId, setBusyId] = useState<string | null>(null);
  const template = TEMPLATES_CATALOG.find((t) => t.id === selectedId);

  const handleUse = async (t: Template) => {
    if (busyId || usedIds.has(t.id)) return;
    setBusyId(t.id);
    const created = await onUseTemplate(t);
    setBusyId(null);
    if (!created) return;
    setUsedIds((prev) => new Set(prev).add(t.id));
    toast.success(`${t.name} added to Your agents`, {
      description: "Open it to rename, rewrite instructions, or swap apps.",
    });
  };

  const statusFor = (id: string): UseStatus => (busyId === id ? "busy" : usedIds.has(id) ? "done" : "idle");

  return (
    <main className="flex-1 min-w-0 flex flex-col bg-[var(--canvas)]">
      {template ? (
        <TemplateDetail
          key={template.id}
          template={template}
          onBack={() => setSelectedId(null)}
          status={statusFor(template.id)}
          onUse={handleUse}
        />
      ) : (
        <TemplatesGrid onOpen={setSelectedId} statusFor={statusFor} onUse={handleUse} />
      )}
    </main>
  );
}
